// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { ModalProps as MantineModalProps } from '@mantine/core';
import { Modal as MantineModal } from '@mantine/core';
import cx from 'clsx';
import type { CSSProperties, JSX, ReactNode } from 'react';
import { Form } from '../Form/Form';
import classes from './Modal.module.css';

/**
 * Keeps the flex chain intact across the form element, so the footer stays pinned. Inline rather
 * than in the CSS module because `Form` takes a style but not a className.
 */
const formStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 auto',
  minHeight: 0,
};

/**
 * Props for {@link Modal}.
 */
export interface ModalProps extends Omit<MantineModalProps, 'children' | 'onSubmit' | 'scrollAreaComponent'> {
  /** Modal body, rendered in the scrolling region between the header and the footer. */
  readonly children: ReactNode;
  /**
   * Buttons pinned to the bottom edge of the modal above a full-bleed border.
   * They stretch to the full width with the primary action first; wrap them in a
   * `Group justify="flex-end"` for a right-aligned row instead. Omit for modals that only display
   * content - the footer and its border are then not rendered.
   */
  readonly actions?: ReactNode;
  /**
   * Wraps the body and the footer in a {@link Form}, so a `SubmitButton` in
   * `actions` submits the named inputs in `children`. A `SubmitButton` without this has no form to
   * submit and does nothing.
   */
  readonly onSubmit?: (formData: Record<string, string>) => Promise<void> | void;
  /**
   * Fixed body height as a CSS length, e.g. `'60vh'`, for modals whose content
   * is a fixed layout rather than a form that should size to its fields. Omit to size to the content.
   */
  readonly bodyHeight?: string;
}

/**
 * A modal with the standard Medplum chrome: a bold title above a border, one scrolling body, and
 * action buttons pinned to the bottom edge. Owns the layout so call sites pass content and actions
 * rather than assembling a shell out of `Stack`, `Divider` and `styles` overrides.
 *
 * All other Mantine `Modal` props pass through. `scrollAreaComponent` does not, because it inserts
 * an element between the content and the body that breaks the flex chain the pinned footer needs.
 * @param props - The Modal React props.
 * @returns The Modal React node.
 */
export function Modal(props: ModalProps): JSX.Element {
  const {
    children,
    actions,
    onSubmit,
    bodyHeight,
    classNames,
    style,
    closeButtonProps,
    centered = true,
    padding = 'lg',
    radius = 'md',
    ...modalProps
  } = props;

  const contents = (
    <>
      <div className={classes.scroll}>{children}</div>
      {actions && <div className={classes.footer}>{actions}</div>}
    </>
  );

  return (
    <MantineModal
      {...modalProps}
      centered={centered}
      padding={padding}
      radius={radius}
      closeButtonProps={{ 'aria-label': 'Close', radius: 'xl', ...closeButtonProps }}
      classNames={{
        ...classNames,
        // The slot rules are scoped under `.modal` in the CSS module, so the root class has to
        // land for any of them to apply.
        root: cx(classes.modal, classNames?.root),
        content: cx(classes.content, classNames?.content),
        header: cx(classes.header, classNames?.header),
        title: cx(classes.title, classNames?.title),
        body: cx(classes.body, classNames?.body),
      }}
      style={bodyHeight ? [style, { '--medplum-modal-body-height': bodyHeight }] : style}
    >
      {onSubmit ? (
        <Form onSubmit={onSubmit} style={formStyle}>
          {contents}
        </Form>
      ) : (
        contents
      )}
    </MantineModal>
  );
}
